// VerReporteDiario.js
// Vista de solo lectura del reporte diario ya enviado hoy para un piso
// (se abre al pulsar un piso marcado como "✅ Hecho" en Asistenta.js).
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { ESTRELLAS_INFO, numEstrellas, textoEstrellas } from "../../utils/estrellas";

const SEMAFORO_INFO = {
  verde: { label: "Bien", bg: "#16a34a", emoji: "🟢" },
  amarillo: { label: "Regular", bg: "#d97706", emoji: "🟡" },
  rojo: { label: "Mal", bg: "#dc2626", emoji: "🔴" },
  desconozco: { label: "Desconozco", bg: "#94a3b8", emoji: "❓" },
};

const SI_NO_INFO = {
  si: { label: "Sí", bg: "#16a34a", emoji: "✅" },
  no: { label: "No", bg: "#dc2626", emoji: "❌" },
};

const RespuestaVista = ({ pregunta, valor, alumnos }) => {
  if (valor === undefined || valor === null || valor === "") {
    return <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Sin respuesta</span>;
  }
  if (pregunta.tipo === "semaforo" || pregunta.tipo === "semaforo+desconozco") {
    const info = SEMAFORO_INFO[valor];
    if (!info) return <span>{valor}</span>;
    return (
      <span style={{ ...s.badge, background: info.bg }}>{info.emoji} {info.label}</span>
    );
  }
  if (pregunta.tipo === "estrellas") {
    const n = numEstrellas(valor);
    if (n === null) return <span>{String(valor)}</span>;
    return <span style={{ ...s.badge, background: ESTRELLAS_INFO[n].bg }}>{textoEstrellas(n)} {n}/5 · {ESTRELLAS_INFO[n].label}</span>;
  }
  if (pregunta.tipo === "si_no") {
    const info = SI_NO_INFO[valor];
    if (!info) return <span>{valor}</span>;
    return (
      <span style={{ ...s.badge, background: info.bg }}>{info.emoji} {info.label}</span>
    );
  }
  if (pregunta.tipo === "opciones_color" && Array.isArray(valor)) {
    return (
      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6 }}>
        {valor.map((id) => {
          const op = (pregunta.valores || []).find((v) => String(v.id) === String(id));
          return <span key={id} style={{ background: op?.color || "#94a3b8", color: "#fff", borderRadius: 999, padding: "3px 10px", fontWeight: 700 }}>{op ? op.etiqueta : id}</span>;
        })}
      </span>
    );
  }
  if (pregunta.tipo === "opciones_color") {
    const op = (pregunta.valores || []).find((v) => String(v.id) === String(valor));
    if (!op) return <span>{valor}</span>;
    return <span style={{ ...s.badge, background: op.color || "#3b82f6" }}>{op.etiqueta}</span>;
  }
  // texto u otros: mostrar tal cual
  if (pregunta.tipo === "seleccion_residentes") {
    if (valor === "ninguno") return <span style={{ display: "inline-block", background: "#dcfce7", color: "#166534", borderRadius: 999, padding: "3px 12px", fontWeight: 800 }}>✓ Ninguno</span>;
    const nombres = (alumnos && alumnos.length ? alumnos.map((a) => a.nombre) : String(valor).split(", "));
    return (
      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6 }}>
        {nombres.map((n, i) => <span key={i} style={{ background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd", borderRadius: 999, padding: "3px 10px", fontWeight: 700 }}>🧑 {n}</span>)}
      </span>
    );
  }
  return <span style={{ whiteSpace: "pre-wrap" }}>{valor}</span>;
};

const VerReporteDiario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const rol = user?.tipo === "asistenta" ? "asistenta" : "supervisora";
  const piso = location.state?.piso;
  // Desde Reportes (supervisora): reporte de un residente o de una sustitución ya enviado
  const modo = location.state?.modo || null;
  const alumno = location.state?.alumno || null;
  const idSustitucion = location.state?.id_sustitucion || null;
  const fechaDia = location.state?.fecha_dia || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reporte, setReporte] = useState(null); // { enviado, usuario, fecha_hora, respuestas }
  const [preguntas, setPreguntas] = useState([]);
  const [alumnosPiso, setAlumnosPiso] = useState([]);

  useEffect(() => {
    if (!piso) return;
    const pedirReporte = modo
      ? axios.get("/api/tareas/reporte", { params: { modo, id_alumno: alumno?.id_alumno, id_sustitucion: idSustitucion, fecha_dia: fechaDia } })
      : axios.get(`/api/servicio/reportes/hoy?id_piso=${piso.id}`);
    Promise.all([
      pedirReporte,
      // incluye también las preguntas desactivadas: si se desactiva una después de enviar, el reporte se sigue viendo completo
      axios.get(`/api/servicio/reporte_opciones?a_qn=${modo || rol}`),
      axios.get(`/api/servicio/alumnos_piso/${piso.id}`).catch(() => ({ data: [] })),
    ])
      .then(([resRep, resPreg, resAl]) => {
        setReporte(resRep.data || null);
        setPreguntas(resPreg.data || []);
        setAlumnosPiso(resAl.data || []);
        setError("");
      })
      .catch(() => setError("❌ No se pudo cargar el reporte."))
      .finally(() => setLoading(false));
  }, [piso, rol, modo, alumno, idSustitucion, fechaDia]);

  // Las respuestas se muestran en el ORDEN configurado de las preguntas (no en el orden en que se contestaron);
  // las de cada alumno (preguntas por residente), una detrás de otra por alumno.
  const respuestasOrdenadas = useMemo(() => {
    const pos = new Map(preguntas.map((p, i) => [p.id, i]));
    const posAl = new Map(alumnosPiso.map((a, i) => [a.id_alumno, i]));
    return [...(reporte?.respuestas || [])]
      .map((r, i) => ({ r, i }))
      .sort((a, b) => (pos.get(a.r.id_pregunta) ?? 1e6) - (pos.get(b.r.id_pregunta) ?? 1e6)
        || (posAl.get(a.r.id_alumno) ?? 1e6) - (posAl.get(b.r.id_alumno) ?? 1e6) || a.i - b.i)
      .map((x) => x.r);
  }, [reporte, preguntas, alumnosPiso]);

  if (!piso) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p style={{ color: "#dc2626" }}>No se ha indicado el piso. Vuelve atrás.</p>
        <button onClick={() => navigate(-1)} style={s.btnVolver}>← Volver</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8" }}>
      <div style={{ background: "#1e3a5f", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.75 }}>{modo === "residente" ? "Reporte de residente" : modo === "sustitucion" ? "Reporte de sustitución" : "Reporte de hoy"}{fechaDia && modo ? ` · ${new Date(fechaDia + "T12:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long" })}` : ""}</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{piso.abreviatura}{alumno ? ` · ${alumno.nombre}` : ""}{modo === "sustitucion" && location.state?.sustituta ? ` · ${location.state.sustituta}` : ""}</div>
        </div>
      </div>

      <div style={{ maxWidth: 540, margin: "0 auto", padding: "20px 20px 40px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Cargando…</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 40, color: "#dc2626" }}>{error}</div>
        ) : !reporte?.enviado ? (
          <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
            No hay ningún reporte guardado hoy para este piso.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              Enviado por <strong>{reporte.usuario}</strong>
              {reporte.fecha_hora && ` a las ${new Date(reporte.fecha_hora).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`}
            </div>
            {respuestasOrdenadas.map((r, i) => {
              const pregunta = preguntas.find((p) => p.id === r.id_pregunta);
              if (!pregunta) return null;
              const alum = r.id_alumno ? alumnosPiso.find((a) => a.id_alumno === r.id_alumno) : null;
              return (
                <div key={i} style={s.fila}>
                  <div style={{ fontSize: 13, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {pregunta.categoria}
                  </div>
                  {r.id_alumno && <div style={{ display: "inline-block", background: "#ede9fe", color: "#5b21b6", borderRadius: 8, padding: "2px 10px", fontSize: 13, fontWeight: 800, margin: "4px 0" }}>🧑 {alum ? alum.nombre : `Alumno ${r.id_alumno}`}</div>}
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1e3a5f", marginBottom: 8 }}>
                    {pregunta.emoji ? `${pregunta.emoji} ` : ""}{pregunta.subcategoria}
                  </div>
                  <RespuestaVista pregunta={pregunta} valor={r.valor} alumnos={r.alumnos} />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

const s = {
  btnVolver: {
    padding: "10px 22px", borderRadius: 20,
    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    color: "#fff", border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: 15,
    boxShadow: "0 2px 8px rgba(29,78,216,0.35)",
  },
  fila: {
    background: "#fff", borderRadius: 14, padding: "14px 16px", marginBottom: 12,
    border: "1px solid #e2e8f0",
  },
  badge: {
    display: "inline-block", color: "#fff", fontWeight: 700, fontSize: 14,
    padding: "6px 14px", borderRadius: 999,
  },
};

export default VerReporteDiario;
